import t from '@/lib/i18next';
import { getAveragePayConfig } from '@/modules/people/utils/get-average-pay-config';

type PayRuleWithConfig = Parameters<typeof getAveragePayConfig>[0];

const LOOKBACK_UNITS = ['DAILY', 'WEEKLY', 'MONTHLY'] as const;
type LookbackUnit = (typeof LOOKBACK_UNITS)[number];

function isLookbackUnit(value: string): value is LookbackUnit {
  return LOOKBACK_UNITS.some(unit => unit === value);
}

function parseLookback(
  lookback?: string
): { unit: LookbackUnit; interval: number } | undefined {
  // RFC 5545 separates parts with ';' only; URLSearchParams below would also split on '&'
  if (!lookback || lookback.includes('&')) {
    return undefined;
  }
  const parts = new URLSearchParams(lookback.split(';').join('&'));
  const unit = parts.get('FREQ');
  const interval = parts.get('INTERVAL') ?? '1';
  const keys = [...parts.keys()];
  const hasOnlyFreqAndInterval = keys.every(
    key => key === 'FREQ' || key === 'INTERVAL'
  );
  const hasEachKeyOnce = new Set(keys).size === keys.length;
  const count = Number(interval);
  if (
    !hasOnlyFreqAndInterval ||
    !hasEachKeyOnce ||
    !unit ||
    !isLookbackUnit(unit) ||
    !/^[1-9]\d*$/.test(interval) ||
    !Number.isSafeInteger(count)
  ) {
    return undefined;
  }
  return { unit, interval: count };
}

function formatLookbackPeriod(lookback?: string): string | undefined {
  const parsed = parseLookback(lookback);
  if (!parsed) {
    return undefined;
  }
  return t(
    `people:people-form-tab-employment-edit:pay-section:average-pay-lookback-${parsed.unit}`,
    { count: parsed.interval }
  );
}

export function isAveragePayRule(payRule: PayRuleWithConfig): boolean {
  const method = getAveragePayConfig(payRule)?.method;
  return method === 'PERCENT_OF_TOTAL_PAY' || method === 'AVERAGE_PER_DAY';
}

export function getAveragePayRuleText(
  payRule: PayRuleWithConfig
): string | undefined {
  if (!isAveragePayRule(payRule)) {
    return undefined;
  }
  const config = getAveragePayConfig(payRule);
  const period = formatLookbackPeriod(config?.lookback);
  if (!period) {
    return t('people:people-form-tab-employment-edit:pay-section:average-pay');
  }
  if (config?.method === 'AVERAGE_PER_DAY') {
    return t(
      'people:people-form-tab-employment-edit:pay-section:average-pay-per-day',
      { period }
    );
  }
  if (!Number.isFinite(config?.percentValue)) {
    return t('people:people-form-tab-employment-edit:pay-section:average-pay');
  }
  return t(
    'people:people-form-tab-employment-edit:pay-section:average-pay-percent',
    { percent: config?.percentValue, period }
  );
}
